import React from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '../App';
import { Users, MessageSquare, Heart, Handshake, ArrowRight, Star } from 'lucide-react';

export default function EliteCommunityPage() {
  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-blue-950/50 to-background">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600/20 border border-blue-600/40 rounded-full mb-6">
            <Users className="w-4 h-4 text-blue-400" />
            <span className="text-blue-400 text-sm font-bold uppercase tracking-wider">Community</span>
          </div>

          <h1 className="text-5xl sm:text-6xl font-heading font-bold mb-6">
            Join the Community
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-10">
            Connect with serious poker players, share experiences, and grow together in a supportive environment.
          </p>
        </div>
      </section>

      {/* Community Benefits */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-heading font-bold text-center mb-16">
            Why Our Community is Different
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-card border border-border rounded-2xl p-8 hover:border-primary/50 transition-all">
              <div className="w-16 h-16 bg-blue-600/10 rounded-xl flex items-center justify-center mb-6">
                <MessageSquare className="w-8 h-8 text-blue-500" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Active Forum</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Post your hands, ask questions, and get detailed feedback from both members and instructors.
              </p>
              <Link to="/community" className="text-primary hover:text-primary/80 font-semibold flex items-center gap-2">
                Visit Forum
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <Link to="/community" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/50 transition-all cursor-pointer" data-testid="community-supportive-culture">
              <div className="w-16 h-16 bg-green-600/10 rounded-xl flex items-center justify-center mb-6">
                <Heart className="w-8 h-8 text-green-500" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Supportive Culture</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                No judgment, no toxicity. Just players helping players improve and enjoy the game.
              </p>
            </Link>

            <Link to="/booking" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/50 transition-all cursor-pointer" data-testid="community-network-connect">
              <div className="w-16 h-16 bg-purple-600/10 rounded-xl flex items-center justify-center mb-6">
                <Handshake className="w-8 h-8 text-purple-500" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Network & Connect</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Find poker buddies, organize home games, and build lasting friendships.
              </p>
            </Link>

            <Link to="/features/coaching" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/50 transition-all cursor-pointer" data-testid="community-learn-from-winners">
              <div className="w-16 h-16 bg-yellow-600/10 rounded-xl flex items-center justify-center mb-6">
                <Star className="w-8 h-8 text-yellow-500" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Learn from Winners</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Get insights from players who've been where you are and reached the next level.
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-card/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-5xl font-bold text-primary mb-2">500+</div>
              <div className="text-muted-foreground">Active Members</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-primary mb-2">1000+</div>
              <div className="text-muted-foreground">Forum Threads</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-primary mb-2">5000+</div>
              <div className="text-muted-foreground">Hands Reviewed</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-primary mb-2">24/7</div>
              <div className="text-muted-foreground">Community Support</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-heading font-bold mb-6">
            Become Part of Something Special
          </h2>
          <p className="text-xl text-muted-foreground mb-10">
            Join a community of passionate poker players today.
          </p>
          <Link
            to="/membership"
            className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-black px-10 py-5 rounded-xl text-lg font-bold shadow-lg transition-all"
          >
            Join Community
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
